// Hand-authored, framework-agnostic origin map for the Vellum API host.
//
// This lives at the package root (not under `src/`) on purpose: `src/` is
// wiped and regenerated by `@hey-api/openapi-ts` (see scripts/generate-client.sh),
// so anything hand-written there would be lost. Keeping it here lets both the
// Vite micro apps and the Luna Next app share a single source of truth for the
// vellum-api origin instead of each hardcoding the URLs.

// Structurally identical to `HostedEnvironment` in `@vellum/ui/environment`,
// but spelled out rather than imported: `@vellum/ui` depends on
// `@vellum/micro-app-auth`, which depends on this package, so importing from
// `@vellum/ui` here would close that loop into a package cycle. The two are
// mutually assignable, so consumers can mix them freely.
export type VellumApiEnvironment = "local" | "dev" | "prod";

/**
 * Public origin for the Vellum API — the shared auth + feature-flag host — per
 * deployment environment. The values are identical across every consumer, so
 * they live here rather than being duplicated per app.
 */
export const VELLUM_API_ORIGINS: Record<VellumApiEnvironment, string> = {
  local: "http://vellum-api.local.vibestack.cloud",
  dev: "https://vellum-api.dev.vellum.health",
  prod: "https://vellum-api.vellum.health",
};

/**
 * Resolve the vellum-api origin for an environment name. Unknown/undefined
 * values fall back to `local`. Each app resolves the environment name in its
 * own (framework-specific) way and passes it in.
 */
export function vellumApiOriginForEnv(env: string | undefined | null): string {
  const key = env?.toLowerCase();
  if (key === "prod") return VELLUM_API_ORIGINS.prod;
  if (key === "dev") return VELLUM_API_ORIGINS.dev;
  return VELLUM_API_ORIGINS.local;
}
